"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223731" className="hidden md:flex gap-8">
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded-full" />
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded-full" />
        <div className="h-4 w-16 bg-muted/20 animate-pulse rounded-full" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground hover:text-foreground p-0">
              <Link to={item.href || "#"} className="group relative font-default text-[13px] font-medium uppercase tracking-[0.2em] text-foreground transition-colors">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-2xl border-b border-border/10 md:hidden shadow-2xl">
      <nav className="flex flex-col p-8 gap-8">
        {loading ? <div className="flex flex-col gap-8">
            <div className="h-6 w-32 bg-muted/20 animate-pulse rounded-full" />
            <div className="h-6 w-32 bg-muted/20 animate-pulse rounded-full" />
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="group relative w-fit font-default text-lg font-semibold uppercase tracking-widest text-foreground" onClick={() => setIsOpen(false)}>
              {item.label}
              <span className="absolute -bottom-1 left-0 h-[1px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
            </Link>)}
      </nav>
    </div>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ease-in-out text-foreground ${scrolled ? "bg-background/80 backdrop-blur-md shadow-md" : "bg-transparent"}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex-shrink-0">
          <Link to="/OceanWalletPage#" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1636402/img/muse_custom_vector_logo.png" alt="OceanWallet" className="h-6 w-auto object-contain brightness-0 invert" />
          </Link>
        </div>

        <MenuProvider menu_id="16">
          <DesktopMenu />
          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-white/10 rounded-full transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>

          <MobileMenu isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} />
        </MenuProvider>
      </div>
    </section>;
}